import { Fragment, useEffect } from "react";
import { useInView } from "react-intersection-observer";

import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import GoodieLogo from "@/design-system/icons/goodie";
import { FetchGet, FetchPost } from "@/lib/fetch-helper";
import OnetPoczta from "@/src/common/icons/OnetPoczta.svg";
import { useMutationCustom, useQueryClientCustom, useQueryCustom } from "@/src/query-client-overrides";
import {
    ConsentScreenType,
    type IOnetConsentItem,
    type IOnetConsentSaveInput,
} from "@/src/shared/onet-consent-modal/contracts";
import ModalV2 from "@Components/ModalV2";
import { ConsentGetOnet, ConsentOnetSave } from "@Routes/ConsentLinks";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QueryXsMax } from "./../../../styles/tokens.stylex";

const onetGetConsentQueryKey = ["onet-consent-get"];

export default function OnetConsentModal({ onConsentChange }: { onConsentChange: () => void }) {
    const queryClient = useQueryClientCustom();
    const { ref, inView } = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const { data, isFetched } = useQueryCustom({
        queryKey: onetGetConsentQueryKey,
        queryFn: () => FetchGet<IOnetConsentItem[]>(ConsentGetOnet),
        refetchOnMount: false,
        retry: false,
        initialData: undefined,
    });

    useEffect(() => {
        if (isFetched && data && data.length === 0) {
            onConsentChange();
        }
    }, [isFetched, data, onConsentChange]);

    const { mutate } = useMutationCustom({
        mutationKey: ["onet-consent-accept"],
        mutationFn: (isApproved: boolean) =>
            FetchPost(ConsentOnetSave, {
                screenType: ConsentScreenType.Onet,
                consentId: data?.[0]?.id,
                isApproved: isApproved,
            } satisfies IOnetConsentSaveInput),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: onetGetConsentQueryKey });
            onConsentChange();
        },
        retry: 1,
    });

    const acceptClick = () => {
        mutate(true);
    };

    const declineClick = () => {
        mutate(false);
    };

    const header = (
        <h1 {...stylex.props(styles.headerImages)}>
            <img src={OnetPoczta} alt="Onet Poczta" {...stylex.props(styles.images)} /> <span>&#43;</span>
            <span {...stylex.props(styles.images)}>
                <GoodieLogo />
            </span>
        </h1>
    );
    if (isFetched && data && data.length === 0) {
        return null;
    }
    return (
        <ModalV2
            headerStyleX={styles.modalHeader}
            header={header}
            sectionStyleX={[styles.modalSection, inView && styles.modalAnimated]}
            onClose={onConsentChange}
            mainStyleX={styles.modalContainer}
            ref={ref}
        >
            <Typography textElement="h2" fontStyle="hS">
                AKTUALIZACJA TREŚCI ZGÓD
            </Typography>

            <Typography textElement="h3" fontStyle="hXS">
                Aby być na bieżąco kliknij „Zgadzam się” poniżej.
            </Typography>

            {Array.from(data || []).map((consent) => (
                <Fragment key={consent.id}>
                    {consent.longDescription && (
                        <Typography textElement="p" fontStyle="BodyM">
                            {consent.longDescription}
                        </Typography>
                    )}
                    {consent.shortDescription && (
                        <Typography key={consent.id} textElement="p" fontStyle="BodyM">
                            {consent.shortDescription}
                        </Typography>
                    )}
                </Fragment>
            ))}

            <div {...stylex.props(styles.buttonContainer)}>
                <Button as="button" size="M" variant="magentaRound" onClick={acceptClick}>
                    Zgadzam się
                </Button>

                <Button as="button" size="M" variant="transparent" onClick={declineClick}>
                    Nie zgadzam się
                </Button>
            </div>
        </ModalV2>
    );
}

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    modalContainer: {
        gap: ComposableSpacing.s6,
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        textAlign: "center",
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },
    modalSection: {
        padding: { default: ComposableSpacing.s6, [xsMax]: ComposableSpacing.s4 },
        borderRadius: { default: "1rem", [xsMax]: 0 },

        alignItems: { default: null, [xsMax]: "center" },
        opacity: 0,
        transform: "translateY(30px)",
        transitionDuration: "0.5s",
        transitionProperty: "opacity, transform",
        transitionTimingFunction: "ease-out",
    },
    modalAnimated: {
        opacity: 1,
        transform: "translateY(0)",
        width: { default: "600px", [xsMax]: "100%" },
    },
    headerImages: {
        gap: "10px",
        alignItems: "center",
        display: "flex",
        fontSize: "1.5rem",
        height: "30px",
    },

    images: {
        height: "30px",
    },

    buttonContainer: {
        gap: ComposableSpacing.s2,
        display: "flex",
        paddingBottom: ComposableSpacing.s2,
    },

    modalHeader: {
        justifyContent: "center",
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
    },
});
